import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useUserOptional } from "@/auth/UserContext";
import { filterSlashCommandsForClient } from "@/features/kai/kaiClientPortal";
import { FALLBACK_SLASH_COMMANDS } from "@/features/kai/kaiSlashCommands";
import { kaiApi, type KaiSlashCommand } from "@/services/kaiApi";

/**
 * Session cache of the `/` slash-command catalogue.
 *
 * The catalogue is RBAC- and route-filtered server-side, so it is keyed by
 * `userId::route`. The result is cached per key so opening the composer on the
 * same page (Kai page remount, side rail toggle) reuses it, and in-flight work
 * is shared to survive a Strict Mode remount.
 */
const cache = new Map<string, KaiSlashCommand[]>();
const inflight = new Map<string, Promise<KaiSlashCommand[] | null>>();

function cacheKey(userId: string, route: string): string {
  return `${userId}::${route}`;
}

function loadSlashCommands(
  userId: string,
  route: string,
): Promise<KaiSlashCommand[] | null> {
  const key = cacheKey(userId, route);
  const hit = cache.get(key);
  if (hit) return Promise.resolve(hit);
  const pending = inflight.get(key);
  if (pending) return pending;

  const request = kaiApi
    .getSlashCommands(route)
    .then((commands) => {
      cache.set(key, commands);
      return commands;
    })
    .catch((err) => {
      console.warn("Failed to fetch Kai slash commands:", err);
      return null;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, request);
  return request;
}

/** Test helper — clears the session cache and any in-flight request. */
export function resetKaiSlashCommandCache(): void {
  cache.clear();
  inflight.clear();
}

/**
 * Resolve the `/` slash-command catalogue for the current user and route.
 *
 * Renders the offline fallback immediately, then swaps in the server-filtered
 * catalogue once it loads. Re-fetches when the route changes so route-gated
 * commands (Home / dashboard / feed filter) appear only where they apply.
 */
export function useKaiSlashCommands(): KaiSlashCommand[] {
  const userCtx = useUserOptional();
  const { pathname } = useLocation();
  const userId = userCtx?.profile?.id ?? null;
  const isClient = userCtx?.isClient ?? false;
  const [commands, setCommands] = useState<KaiSlashCommand[]>(() =>
    filterSlashCommandsForClient(FALLBACK_SLASH_COMMANDS, isClient),
  );

  useEffect(() => {
    if (!userId) return;
    // Show any cached catalogue for this key synchronously on the next tick.
    const cached = cache.get(cacheKey(userId, pathname));
    if (cached) {
      setCommands(filterSlashCommandsForClient(cached, isClient));
      return;
    }
    let cancelled = false;
    void loadSlashCommands(userId, pathname).then((next) => {
      if (!cancelled && next) {
        setCommands(filterSlashCommandsForClient(next, isClient));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId, pathname, isClient]);

  return commands;
}
